import {useQuery} from '@tanstack/react-query'
import {useDialog} from '@/hooks/useDialog'
import {
    Fingerprint,
    Hash,
    Layers,
    User,
    Play,
    Pause,
    XCircle,
    CheckCircle,
    ListTree,
    Shield,
    Clock,
    ChevronDown,
    ChevronRight,
    CornerDownRight,
    History,
    KeyRound,
    RefreshCw,
    Loader2,
    Plus,
    Power,
    PowerOff,
    Trash2,
} from 'lucide-react'
import {useParams, Link} from 'react-router-dom'
import {useCalculationStatus} from '@/hooks/useCalculationStatus'
import {routes} from '@/lib/routes'
import {notFoundIfMissing} from '@/lib/not-found-error'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {useIdentity, IDENTITY_KEY, IDENTITIES_KEY} from '@/features/identity/hooks/useIdentity'
import {useIdentityPermissions} from '@/features/identity/hooks/useIdentityPermissions'
import {useIdentityMutations} from '@/features/identity/hooks/useIdentityMutations'
import {useIdentityDetailTab} from '@/features/identity/hooks/useIdentityDetailTab'
import {Strong} from '@/components/shared/Strong'
import {InlineRow} from '@/components/shared/InlineRow'
import {Stack} from '@/components/shared/Stack'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {formatDate, formatDateOnly, formatDateShort, formatTimestamp, isMaxInstant, toInstantIso} from '@/lib/date-utils'
import {Button} from '@/components/ui/button'
import {Card, CardHeader, CardTitle, CardDescription, CardContent} from '@/components/ui/card'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {Skeleton} from '@/components/ui/skeleton'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {Fragment, useCallback, useMemo, useRef, useState} from 'react'
import {IdentityLifecycleState} from '@/types/identity'
import type {IdentityLifecycleRequest} from '@/types/identity'
import {Input} from '@/components/ui/input'
import {DatePicker} from '@/components/shared/DatePicker'
import {FormField} from '@/components/shared/FormField'
import {AssignmentStatus, AssignmentEventType, AssignmentSourceType} from '@/types/assignment'
import {FunctionGroupAssignmentStatus} from '@/types/function-group'
import {AccessProfileStatus} from '@/types/access-profile'
import {AccountStatus} from '@/types/account'
import type {Account} from '@/types/account'
import {ReasonField} from '@/components/account/ReasonField'
import type {TimelineEvent} from '@/types/timeline'
import {IdentityLifecycleStateBadge, getAvailableLifecycleActions} from '@/types/identity'
import {
    assignmentEventTypeColor,
    assignmentSourceTypeIcon,
    useAssignmentEventTypeLabel,
    useAssignmentSourceTypeLabel,
    useFunctionGroupAssignmentStatusLabel,
    useIdentityLifecycleActionLabel,
} from '@/features/identity/labels'
import {FunctionGroupAssignmentStatusBadge} from '@/types/function-group'
import {AccountStatusBadge} from '@/types/account'
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {AccessTimeline} from '@/components/identity/AccessTimeline'
import {IdentityManagerSection} from '@/components/identity/IdentityManagerSection'
import {TimelineDetailModal} from '@/components/identity/TimelineDetailModal'
import {IdentityLifecycleHeader} from '@/components/identity/IdentityLifecycleHeader'
import {IdentityLifecycleTimeline} from '@/components/identity/IdentityLifecycleTimeline'
import {Textarea} from '@/components/ui/textarea'
import {useIdentityLifecycleEvents} from '@/hooks/use-identity-lifecycle-events'
import type {TriggerSource} from '@/types/identity'
import {InlineAttributeEditor} from '@/components/attributes/InlineAttributeEditor'
import {SourcesCard, SourcesEntityType} from '@/components/sources/SourcesCard'
import {AttributeEntityType} from '@/types/attribute-entity-type'
import {CodeText} from '@/components/shared/CodeText'
import {PageLayout} from '@/components/shared/PageLayout'
import {SeverityBadge} from '@/components/violations/SeverityBadge'
import {ViolationSubjectTable} from '@/types/violation'
import {CardSection} from '@/components/shared/CardSection'
import {InlineEmptyText} from '@/components/shared/InlineEmptyText'
import {TextLink} from '@/components/shared/TextLink'
import {EntityLink} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'
import {SmallText} from '@/components/shared/SmallText'
import {IconText} from '@/components/shared/IconText'
import {Icon} from '@/components/shared/Icon'
import {TruncatedCell} from '@/components/shared/TruncatedCell'
import {DetailGrid} from '@/components/shared/DetailGrid'
import {EntityCardLink} from '@/components/shared/EntityCardLink'
import {DetailField} from '@/components/shared/DetailField'
import {HistoryEntry} from '@/components/shared/HistoryEntry'
import {resolveTypeValueIcon} from '@/components/entity-type/entity-type-icon'


/**
 * Format an expiration date, showing "Permanent" for max instant/never-expires dates.
 */
function formatExpiresAt(dateStr: string | null | undefined, i18n: ReturnType<typeof useI18n>, useShortFormat = false): string {
    if (!dateStr) return '—'
    if (isMaxInstant(dateStr)) {
        return String(i18n.common.permanent)
    }
    return useShortFormat ? formatDateShort(dateStr) : formatDate(dateStr)
}

export function IdentityDetail() {
    const services = useServices()
    const i18n = useI18n()
    const lifecycleActionLabel = useIdentityLifecycleActionLabel()
    const fgStatusLabel = useFunctionGroupAssignmentStatusLabel()
    const sourceTypeLabel = useAssignmentSourceTypeLabel()
    const eventTypeLabel = useAssignmentEventTypeLabel()
    const {id} = useParams<{ id: string }>()

    const {
        canUpdate,
        canRehire,
        canRevokeAssignment,
        canCreateAccount,
        canUpdateAccount,
        canDeleteAccount,
        canAssignFg,
    } = useIdentityPermissions()
    const canUpdateLifecycle = canUpdate

    const {
        identity,
        assignments,
        fgAssignments,
        inheritedAssignments,
        inheritedLoading,
        inheritedError,
        inheritedRefetch,
        accounts,
        identifiers,
        timeline,
        timelineLoading,
        isLoading,
        error,
    } = useIdentity(id)

    const {accessTab, lifecycleTab, setAccessTab, setLifecycleTab} = useIdentityDetailTab()
    const lifecycleCardRef = useRef<HTMLDivElement>(null)
    const handleViewTimeline = () => {
        setLifecycleTab('lifecycle')
        lifecycleCardRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'})
    }

    const lifecycleDialog = useDialog()
    const [targetState, setTargetState] = useState<IdentityLifecycleState | null>(null)
    const [lifecycleReason, setLifecycleReason] = useState<string>('')
    const [lifecycleFilters, setLifecycleFilters] = useState<{triggerSources: TriggerSource[]; from: string; to: string}>({triggerSources: [], from: '', to: ''})
    const [suspendReactivationDate, setSuspendReactivationDate] = useState<string>('')
    const reactivationDateDialog = useDialog()
    const [reactivationDateDraft, setReactivationDateDraft] = useState<string>('')
    const rehireDialog = useDialog()
    const [rehireNewEntryDate, setRehireNewEntryDate] = useState<string>('')
    const [rehireReason, setRehireReason] = useState<string>('')
    const [expandedAssignmentId, setExpandedAssignmentId] = useState<string | null>(null)
    const [expandedFgAssignmentId, setExpandedFgAssignmentId] = useState<string | null>(null)
    const [revokeFgAssignmentId, setRevokeFgAssignmentId] = useState<string | null>(null)
    const [selectedTimelineEvent, setSelectedTimelineEvent] = useState<TimelineEvent | null>(null)
    const [revokeAssignmentId, setRevokeAssignmentId] = useState<string | null>(null)

    const createAccountDialog = useDialog()
    const [selectedSystemId, setSelectedSystemId] = useState<string>('')
    const [disableAccountDialog, setDisableAccountDialog] = useState<Account | null>(null)
    const [enableAccountDialog, setEnableAccountDialog] = useState<Account | null>(null)
    const [deleteAccountDialog, setDeleteAccountDialog] = useState<Account | null>(null)
    const [accountActionReason, setAccountActionReason] = useState<string>('')
    const [processingAccountIds, setProcessingAccountIds] = useState<Set<string>>(new Set())

    const {
        identityTypes,
        lifecycleStates,
        accountStatuses,
        assignmentStatuses,
        accessProfileStatuses,
        systemTypes,
        environmentTypes,
        identifierTypes,
    } = useAppConfig()

    const scrollToFgAssignment = useCallback((fgId: string) => {
        const match = fgAssignments.find(a => a.functionGroupId === fgId)
        if (match) {
            setExpandedFgAssignmentId(match.id)
        }
        const el = document.getElementById(`fg-assignment-${fgId}`)
        if (el) {
            el.scrollIntoView({behavior: 'smooth', block: 'center'})
        }
    }, [fgAssignments])

    type MergedAssignmentRow =
        | { kind: 'direct'; sortKey: string; data: typeof assignments[number] }
        | { kind: 'viaFg'; sortKey: string; data: typeof inheritedAssignments[number] }
    const mergedAssignments: MergedAssignmentRow[] = useMemo(() => {
        const rows: MergedAssignmentRow[] = [
            ...assignments.map(a => ({kind: 'direct' as const, sortKey: (a.accessProfileName ?? '').toLowerCase(), data: a})),
            ...inheritedAssignments.map(i => ({kind: 'viaFg' as const, sortKey: (i.accessProfile.displayName ?? '').toLowerCase(), data: i})),
        ]
        return rows.sort((a, b) => a.sortKey.localeCompare(b.sortKey))
    }, [assignments, inheritedAssignments])

    const lifecycleHeaderQuery = useIdentityLifecycleEvents(id, {size: 1})

    const lifecycleTimelineQuery = useIdentityLifecycleEvents(id, {
        triggerSource: lifecycleFilters.triggerSources.length > 0 ? lifecycleFilters.triggerSources : undefined,
        from: lifecycleFilters.from ? toInstantIso(lifecycleFilters.from) : undefined,
        to: lifecycleFilters.to ? toInstantIso(lifecycleFilters.to) : undefined,
        size: 50,
    })

    const [isCalculating, setIsCalculating] = useState(false)
    const onCalculationComplete = useCallback(() => setIsCalculating(false), [])
    useCalculationStatus(id, [[IDENTITY_KEY, id!], [IDENTITIES_KEY]], isCalculating, onCalculationComplete)

    const lifecycleSuccessMessage = targetState === IdentityLifecycleState.ACTIVE
        ? i18n.identities.activationSubmitted
        : targetState === IdentityLifecycleState.TERMINATED
            ? i18n.identities.terminationSubmitted
            : i18n.identities.lifecycleUpdated

    const trackProcessingAccount = useCallback((accountId: string) => {
        setProcessingAccountIds((prev) => new Set(prev).add(accountId))
    }, [])

    const {
        lifecycle: lifecycleMutation,
        rehire: rehireMutation,
        reactivationDate: reactivationDateMutation,
        evaluateRules: evaluateRulesMutation,
        revoke: revokeMutation,
        revokeFg: revokeFgMutation,
        createAccount: createAccountMutation,
        provisionNow: provisionNowMutation,
        disableAccount: disableAccountMutation,
        enableAccount: enableAccountMutation,
        deleteAccount: deleteAccountMutation,
    } = useIdentityMutations(id, {
        accountActionReason,
        lifecycleSuccessMessage,
        closeLifecycleDialog: lifecycleDialog.close,
        closeRehireDialog: rehireDialog.close,
        closeReactivationDateDialog: reactivationDateDialog.close,
        closeRevokeAssignmentDialog: () => setRevokeAssignmentId(null),
        closeRevokeFgDialog: () => setRevokeFgAssignmentId(null),
        closeCreateAccountDialog: createAccountDialog.close,
        closeDisableAccountDialog: () => setDisableAccountDialog(null),
        closeEnableAccountDialog: () => setEnableAccountDialog(null),
        closeDeleteAccountDialog: () => setDeleteAccountDialog(null),
        onLifecycleSuccess: () => {
            setIsCalculating(true)
            setTargetState(null)
            setSuspendReactivationDate('')
            setLifecycleReason('')
        },
        onRehireSuccess: () => {
            setRehireNewEntryDate('')
            setRehireReason('')
        },
        onEvaluateRulesSuccess: () => {
            setIsCalculating(true)
        },
        onCreateAccountSuccess: () => {
            setSelectedSystemId('')
        },
        onAccountActionSettled: (accountId) => {
            setAccountActionReason('')
            setProcessingAccountIds((prev) => {
                const next = new Set(prev)
                next.delete(accountId)
                return next
            })
        },
    })

    const {data: allSystemsData} = useQuery({
        queryKey: ['systems', 'all-for-account-create'],
        queryFn: () => services.systems.list({size: 100}),
        enabled: !!id && canCreateAccount,
    })

    const eligibleSystems = (allSystemsData?.content || []).filter(
        (system) => !accounts.some((account) => account.targetSystem.id === system.id
            && account.status !== AccountStatus.DELETED)
    )

    const handleLifecycleAction = (state: IdentityLifecycleState) => {
        setTargetState(state)
        setSuspendReactivationDate('')
        setLifecycleReason('')
        lifecycleDialog.open()
    }

    const confirmLifecycleChange = () => {
        if (!identity || !targetState) return
        const trimmedReason = lifecycleReason.trim()
        const request: IdentityLifecycleRequest = {
            lifecycleState: targetState,
            version: identity.version,
        }
        if (targetState === IdentityLifecycleState.SUSPENDED && suspendReactivationDate) {
            request.reactivationDate = suspendReactivationDate
        }
        if (trimmedReason) {
            request.reason = trimmedReason
        }
        lifecycleMutation.mutate(request)
    }

    const submitReactivationDate = () => {
        if (!identity) return
        reactivationDateMutation.mutate({
            reactivationDate: reactivationDateDraft || null,
            version: identity.version,
        })
    }

    const submitRehire = () => {
        if (!identity) return
        rehireMutation.mutate({
            newEntryDate: rehireNewEntryDate,
            reason: rehireReason,
            version: identity.version,
        })
    }

    const openReactivationDateDialog = () => {
        if (!identity) return
        setReactivationDateDraft(identity.reactivationDate ? identity.reactivationDate.substring(0, 10) : '')
        reactivationDateDialog.open()
    }


    // Get action icon
    const getActionIcon = (state: IdentityLifecycleState) => {
        switch (state) {
            case IdentityLifecycleState.ACTIVE:
                return <Icon icon={Play} className="mr-2"/>
            case IdentityLifecycleState.SUSPENDED:
                return <Icon icon={Pause} className="mr-2"/>
            case IdentityLifecycleState.TERMINATED:
                return <Icon icon={XCircle} className="mr-2"/>
        }
    }

    // Get action variant
    const getActionVariant = (state: IdentityLifecycleState): 'default' | 'secondary' | 'destructive' => {
        switch (state) {
            case IdentityLifecycleState.ACTIVE:
                return 'default'
            case IdentityLifecycleState.SUSPENDED:
                return 'secondary'
            case IdentityLifecycleState.TERMINATED:
                return 'destructive'
        }
    }

    // Toggle assignment expansion
    const toggleAssignment = (id: string) => {
        setExpandedAssignmentId(expandedAssignmentId === id ? null : id)
    }

    const toggleFgAssignment = (fgId: string) => {
        setExpandedFgAssignmentId(expandedFgAssignmentId === fgId ? null : fgId)
    }

    const availableActions = identity ? getAvailableLifecycleActions(identity.lifecycleState) : []

    const HeaderIcon = resolveTypeValueIcon(identityTypes, identity?.identityType, Fingerprint)

    return (
        <PageLayout
            variant="detail"
            icon={HeaderIcon}
            title={identity?.party.displayName ?? ''}
            backTo={routes.identityManagement.identities.list()}
            loading={isLoading}
            error={error ?? (!isLoading ? notFoundIfMissing(identity, 'IDENTITY') : undefined)}
            data-testid="identity-detail-page"
            description={identity ? (
                <InlineRow gap="sm" wrap>
                    <Badge variant="outline">
                        {labelOf(identityTypes, identity.identityType)}
                    </Badge>
                    {(() => {
                        const lifecycleBadge = IdentityLifecycleStateBadge.of(identity.lifecycleState)
                        const StateIcon = lifecycleBadge?.icon
                        return (
                            <span data-testid="lifecycle-state-badge" data-lifecycle-state={identity.lifecycleState}>
                                <Badge variant={lifecycleBadge?.variant ?? BadgeVariant.OUTLINE}
                                       className="gap-1"
                                       data-testid={`badge-lifecycle-state-${identity.lifecycleState}`}>
                                    {StateIcon && <StateIcon className="h-3 w-3" aria-hidden="true"/>}
                                    {labelOf(lifecycleStates, identity.lifecycleState)}
                                </Badge>
                            </span>
                        )
                    })()}
                    {isCalculating && (
                        <Badge variant="secondary" data-testid="badge-processing">
                            <Icon icon={RefreshCw} size="xs" className="mr-1 animate-spin"/>
                            {i18n.identities.processing}
                        </Badge>
                    )}
                    <SeverityBadge entityTable={ViolationSubjectTable.IDENTITIES} entityId={identity.id}/>
                </InlineRow>
            ) : undefined}
            actions={identity && canUpdate ? (
                <InlineRow>
                    {availableActions.map((state) => (
                        <Button
                            key={state}
                            variant={getActionVariant(state)}
                            size="sm"
                            onClick={() => handleLifecycleAction(state)}
                            data-testid={`btn-lifecycle-${state}`}
                        >
                            {getActionIcon(state)}
                            {lifecycleActionLabel(state)}
                        </Button>
                    ))}
                    {identity.lifecycleState === IdentityLifecycleState.ACTIVE && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => evaluateRulesMutation.mutate()}
                            disabled={evaluateRulesMutation.isPending}
                            data-testid="btn-evaluate-rules"
                        >
                            <Icon icon={RefreshCw} spin={evaluateRulesMutation.isPending} className="mr-2"/>
                            {i18n.identities.evaluateRules}
                        </Button>
                    )}
                    {identity.lifecycleState === IdentityLifecycleState.TERMINATED && canRehire && (
                        <Button
                            variant="default"
                            size="sm"
                            onClick={() => {
                                setRehireNewEntryDate('')
                                setRehireReason('')
                                rehireDialog.open()
                            }}
                            data-testid="btn-rehire"
                        >
                            <Icon icon={RefreshCw} className="mr-2"/>
                            {i18n.identities.rehire}
                        </Button>
                    )}
                </InlineRow>
            ) : undefined}
        >
        {identity && (
        <>
        <Stack gap="md">
                {/* Details Card — page-wide; line 1: fields, line 2: lifecycle event */}
                <CardSection
                    title={String(i18n.common.details)}
                    description={String(i18n.identities.identityInfo)}
                    data-testid="card-section-details"
                >
                    <Stack gap="md">
                        <dl>
                            <DetailGrid columns={3}>
                                <DetailField label={String(i18n.identities.identityType)} value={labelOf(identityTypes, identity.identityType)}/>
                                {(identity.derivedDisplayName ?? identity.party.displayName) && (
                                    <DetailField label={String(i18n.identities.derivedDisplayName)} value={<span data-testid="field-display-name">{identity.derivedDisplayName ?? identity.party.displayName}</span>}/>
                                )}
                                <DetailField
                                    label={String(i18n.identities.lifecycleState)}
                                    value={(() => {
                                        const lifecycleBadge = IdentityLifecycleStateBadge.of(identity.lifecycleState)
                                        const StateIcon = lifecycleBadge?.icon
                                        return (
                                            <Badge variant={lifecycleBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1">
                                                {StateIcon && <StateIcon className="h-3 w-3" aria-hidden="true"/>}
                                                {labelOf(lifecycleStates, identity.lifecycleState)}
                                            </Badge>
                                        )
                                    })()}
                                />
                                {identity.entryDate && (
                                    <DetailField label={String(i18n.identities.entryDate)} value={formatDateOnly(identity.entryDate)}/>
                                )}
                                {identity.exitDate && (
                                    <DetailField label={String(i18n.identities.exitDate)} value={formatDateOnly(identity.exitDate)}/>
                                )}
                                {identity.lifecycleState === IdentityLifecycleState.SUSPENDED && (identity.reactivationDate || canUpdateLifecycle) && (
                                    <DetailField
                                        label={String(i18n.identities.reactivationDate)}
                                        value={
                                            <InlineRow gap="sm">
                                                <span data-testid="field-reactivation-date">
                                                    {identity.reactivationDate ? formatDateOnly(identity.reactivationDate) : i18n.identities.reactivationDateNone}
                                                </span>
                                                {canUpdateLifecycle && (
                                                    <Button
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={openReactivationDateDialog}
                                                        data-testid="btn-edit-reactivation-date"
                                                    >
                                                        {i18n.common.edit}
                                                    </Button>
                                                )}
                                            </InlineRow>
                                        }
                                    />
                                )}
                            </DetailGrid>
                        </dl>
                        <IdentityLifecycleHeader
                            event={lifecycleHeaderQuery.data?.content?.[0]}
                            isLoading={lifecycleHeaderQuery.isLoading}
                            isError={lifecycleHeaderQuery.isError}
                            onViewTimeline={handleViewTimeline}
                        />
                    </Stack>
                </CardSection>

                {/* Party Card */}
                <CardSection
                    icon={User}
                    title={String(i18n.identities.party)}
                    description={String(i18n.identities.partyBelongsTo)}
                    data-testid="section-party"
                >
                    <EntityCardLink
                        to={routes.identityManagement.parties.detail(identity.party.id)}
                        icon={User}
                        subtitle={i18n.identities.viewPartyDetails}
                        data-testid="link-party-detail"
                    >
                        {identity.party.displayName}
                    </EntityCardLink>
                </CardSection>

                {/* Manager / Responsible Persons Card — epic 986 Phase D, spec §7 */}
                <IdentityManagerSection identityId={id!}/>
            </Stack>

            {/* Sources Section — Spec §6.1 (572.07) */}
            <SourcesCard
                entityType={SourcesEntityType.identities}
                entityId={id!}
                canEdit={canUpdate}
                invalidateOnMutate={[[IDENTITY_KEY, id]]}
            />

            {/* Identifiers Section — issue 947 */}
            <CardSection
                icon={Hash}
                title={String(i18n.identities.identifiersSection)}
                description={String(i18n.identities.identifierCount({count: identifiers.length}))}
                data-testid="card-section-identifiers"
            >
                {identifiers.length > 0 ? (
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead data-testid="col-identifier-type">{i18n.identities.identifierTypeColumn}</TableHead>
                                <TableHead data-testid="col-identifier-value">{i18n.identities.identifierValueColumn}</TableHead>
                                <TableHead data-testid="col-identifier-primary">{i18n.identities.identifierPrimaryColumn}</TableHead>
                                <TableHead data-testid="col-identifier-valid-from">{i18n.identities.identifierValidFromColumn}</TableHead>
                                <TableHead data-testid="col-identifier-valid-to">{i18n.identities.identifierValidToColumn}</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {identifiers.map((idf) => (
                                <TableRow key={idf.id} data-testid={`identifier-row-${idf.id}`} data-readonly>
                                    <TableCell>
                                        {labelOf(identifierTypes, idf.identifierType) || idf.identifierType}
                                    </TableCell>
                                    <TableCell>
                                        <CodeText>{idf.identifierValue}</CodeText>
                                    </TableCell>
                                    <TableCell>
                                        {idf.primary && (
                                            <Badge variant="success" data-testid={`identifier-primary-${idf.id}`}>
                                                {i18n.identities.identifierPrimaryBadge}
                                            </Badge>
                                        )}
                                    </TableCell>
                                    <TableCell>{formatDate(idf.validFrom)}</TableCell>
                                    <TableCell>{idf.validTo ? formatDate(idf.validTo) : '—'}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                ) : (
                    <InlineEmptyText data-testid="identifiers-empty">
                        {i18n.identities.noIdentifiersRecorded}
                    </InlineEmptyText>
                )}
            </CardSection>

            {/* Attributes Section */}
            <CardSection
                icon={ListTree}
                title={String(i18n.common.attributes)}
                data-testid="section-attributes"
            >
                <InlineAttributeEditor
                    entityType={AttributeEntityType.IDENTITY}
                    entityId={id!}
                    entityTypeCode={identity.identityType}
                    canEdit={canUpdate}
                    sources={identity.sources}
                />
            </CardSection>

            {/* Linked Accounts Section */}
            <Card>
                <CardHeader>
                    <InlineRow justify="between">
                        <div>
                            <CardTitle data-testid="accounts-section-title">
                                <IconText icon={KeyRound} size="md">{i18n.identities.accounts}</IconText>
                            </CardTitle>
                            <CardDescription>
                                {i18n.identities.accountCount({count: accounts.length})}
                            </CardDescription>
                        </div>
                        {canCreateAccount && eligibleSystems.length > 0 && (
                            <Button
                                size="sm"
                                onClick={() => {
                                    createAccountDialog.open()
                                    if (eligibleSystems.length === 1) {
                                        setSelectedSystemId(eligibleSystems[0].id)
                                    }
                                }}
                                data-testid="btn-create-account"
                            >
                                <IconText icon={Plus}>{i18n.accounts.createAccount}</IconText>
                            </Button>
                        )}
                    </InlineRow>
                </CardHeader>
                <CardContent>
                    {accounts.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-target-system" className="min-w-[220px]">{i18n.accounts.targetSystem}</TableHead>
                                    <TableHead data-testid="col-external-identifier">{i18n.identities.externalIdentifier}</TableHead>
                                    <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                    <TableHead data-testid="col-created-at">{i18n.common.createdAt}</TableHead>
                                    {(canUpdateAccount || canDeleteAccount) && (
                                        <TableHead data-testid="col-actions" className="text-right">{i18n.common.actions}</TableHead>
                                    )}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {accounts.map((account) => {
                                    const isProcessing = processingAccountIds.has(account.id)
                                    const isLocked = account.status === AccountStatus.LOCKED
                                    const isPending = account.status === AccountStatus.PENDING
                                    const isEnabled = account.status === AccountStatus.ENABLED
                                    const isDisabled = account.status === AccountStatus.DISABLED
                                    const isDeleted = account.status === AccountStatus.DELETED

                                    return (
                                        <TableRow key={account.id} data-testid={`account-row-${account.id}`}>
                                            <TableCell>
                                                <Stack>
                                                    <EntityLink value={account.targetSystem} size="base" testId={`link-system-detail-${account.targetSystem.id}`}/>
                                                    <MutedText size="xs" block>
                                                        {labelOf(systemTypes, account.targetSystemType)} / {labelOf(environmentTypes, account.targetSystem.subType ?? '')}
                                                    </MutedText>
                                                </Stack>
                                            </TableCell>
                                            <TruncatedCell maxWidth="320px">
                                                {isPending ? (
                                                    <MutedText className="italic">
                                                        {i18n.accounts.pendingCreation}
                                                    </MutedText>
                                                ) : (
                                                    <CodeText>{account.externalIdentifier}</CodeText>
                                                )}
                                            </TruncatedCell>
                                            <TableCell>
                                                <InlineRow gap="sm">
                                                    {(() => {
                                                        const statusBadge = AccountStatusBadge.of(account.status)
                                                        const AccIcon = statusBadge?.icon
                                                        const badge = (
                                                            <Badge variant={statusBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1">
                                                                {AccIcon && <AccIcon className="h-3 w-3" aria-hidden="true"/>}
                                                                {labelOf(accountStatuses, account.status)}
                                                            </Badge>
                                                        )
                                                        return isLocked ? (
                                                            <span title={String(i18n.accounts.lockedTooltip)}>{badge}</span>
                                                        ) : badge
                                                    })()}
                                                    {isProcessing && (
                                                        <span title={isPending
                                                            ? String(i18n.accounts.creationInProgress)
                                                            : isEnabled
                                                                ? String(i18n.accounts.disableInProgress)
                                                                : isDisabled
                                                                    ? String(i18n.accounts.enableInProgress)
                                                                    : String(i18n.accounts.deleteInProgress)
                                                        }>
                                                            <Icon icon={Loader2} muted className="animate-spin"/>
                                                        </span>
                                                    )}
                                                </InlineRow>
                                            </TableCell>
                                            <TableCell className="text-muted-foreground">
                                                {formatTimestamp(account.createdAt)}
                                            </TableCell>
                                            {(canCreateAccount || canUpdateAccount || canDeleteAccount) && (
                                                <TableCell className="text-right">
                                                    {!isLocked && !isDeleted && (
                                                        <Stack align="end" gap="xs">
                                                            {/*
                                                             * WHY (issue 736 B.1/B.2): per-identity provision-now affordance
                                                             * for SCIM and LDAP outbound target systems. Re-issues the
                                                             * create-account provisioning task for retries / initial
                                                             * provisioning. Gated by accounts:create which mirrors the
                                                             * existing btn-create-account permission.
                                                             */}
                                                            {canCreateAccount && (isPending || isEnabled || isDisabled) && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    disabled={isProcessing}
                                                                    onClick={() => provisionNowMutation.mutate({
                                                                        identityId: id!,
                                                                        targetSystemId: account.targetSystem.id,
                                                                        accountId: account.id,
                                                                    })}
                                                                    data-testid={`btn-provision-now-${account.id}`}
                                                                    className="w-40 justify-start"
                                                                >
                                                                    <Icon icon={RefreshCw} className="mr-1"/>
                                                                    {i18n.accounts.provisionNow}
                                                                </Button>
                                                            )}
                                                            {canUpdateAccount && isEnabled && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    disabled={isProcessing}
                                                                    onClick={() => setDisableAccountDialog(account)}
                                                                    data-testid={`btn-disable-account-${account.id}`}
                                                                    className="w-40 justify-start"
                                                                >
                                                                    <Icon icon={PowerOff} className="mr-1"/>
                                                                    {i18n.accounts.disableAccount}
                                                                </Button>
                                                            )}
                                                            {canUpdateAccount && isDisabled && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    disabled={isProcessing}
                                                                    onClick={() => setEnableAccountDialog(account)}
                                                                    data-testid={`btn-enable-account-${account.id}`}
                                                                    className="w-40 justify-start"
                                                                >
                                                                    <Icon icon={Power} className="mr-1"/>
                                                                    {i18n.accounts.enableAccount}
                                                                </Button>
                                                            )}
                                                            {canDeleteAccount && (isPending || isEnabled || isDisabled) && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    disabled={isProcessing}
                                                                    onClick={() => setDeleteAccountDialog(account)}
                                                                    data-testid={`btn-delete-account-${account.id}`}
                                                                    className="w-40 justify-start"
                                                                >
                                                                    <Icon icon={Trash2} className="mr-1"/>
                                                                    {isPending
                                                                        ? i18n.accounts.cancelAccountCreation
                                                                        : i18n.accounts.deleteAccount}
                                                                </Button>
                                                            )}
                                                        </Stack>
                                                    )}
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    )
                                })}
                            </TableBody>
                        </Table>
                    ) : (
                        <MutedText block centered className="py-4">
                            <MutedText>{i18n.accounts.noAccountsLinked}</MutedText>
                        </MutedText>
                    )}
                </CardContent>
            </Card>

            {/* Create Account Dialog */}
            <Dialog open={createAccountDialog.isOpen} onOpenChange={createAccountDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.accounts.createAccountTitle}</DialogTitle>
                        <DialogDescription>
                            {selectedSystemId
                                ? i18n.accounts.createAccountMessage({
                                    systemName: eligibleSystems.find((s) => s.id === selectedSystemId)?.name || '',
                                })
                                : i18n.accounts.selectTargetSystem}
                        </DialogDescription>
                    </DialogHeader>
                    <Select value={selectedSystemId} onValueChange={setSelectedSystemId}>
                        <SelectTrigger data-testid="select-target-system">
                            <SelectValue placeholder={i18n.accounts.selectTargetSystem}/>
                        </SelectTrigger>
                        <SelectContent>
                            {eligibleSystems.map((system) => (
                                <SelectItem key={system.id} value={system.id}>
                                    {system.name}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <SaveCancelFooter
                        testIdPrefix="create-account"
                        onCancel={() => {
                            createAccountDialog.close()
                            setSelectedSystemId('')
                        }}
                        saving={createAccountMutation.isPending}
                        disabled={!selectedSystemId}
                        saveLabel={i18n.accounts.createAccount}
                        saveType="button"
                        onSave={() => createAccountMutation.mutate(selectedSystemId)}
                    />
                </DialogContent>
            </Dialog>

            {/* Disable Account Dialog */}
            <Dialog open={!!disableAccountDialog} onOpenChange={(open) => {
                if (!open) {
                    setDisableAccountDialog(null)
                    setAccountActionReason('')
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.accounts.disableAccountTitle}</DialogTitle>
                        <DialogDescription>
                            {i18n.accounts.disableAccountMessage({
                                systemName: disableAccountDialog?.targetSystem.displayName || '',
                            })}
                        </DialogDescription>
                    </DialogHeader>
                    <ReasonField
                        value={accountActionReason}
                        onChange={setAccountActionReason}
                        testIdPrefix="disable-account"
                    />
                    <SaveCancelFooter
                        testIdPrefix="disable-account"
                        onCancel={() => {
                            setDisableAccountDialog(null)
                            setAccountActionReason('')
                        }}
                        saving={disableAccountMutation.isPending}
                        saveLabel={i18n.accounts.disableAccount}
                        destructive
                        saveType="button"
                        onSave={() => {
                            if (!disableAccountDialog) return
                            trackProcessingAccount(disableAccountDialog.id)
                            disableAccountMutation.mutate(disableAccountDialog.id)
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Enable Account Dialog */}
            <Dialog open={!!enableAccountDialog} onOpenChange={(open) => {
                if (!open) {
                    setEnableAccountDialog(null)
                    setAccountActionReason('')
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.accounts.enableAccountTitle}</DialogTitle>
                        <DialogDescription>
                            {i18n.accounts.enableAccountMessage({
                                systemName: enableAccountDialog?.targetSystem.displayName || '',
                            })}
                        </DialogDescription>
                    </DialogHeader>
                    <ReasonField
                        value={accountActionReason}
                        onChange={setAccountActionReason}
                        testIdPrefix="enable-account"
                    />
                    <SaveCancelFooter
                        testIdPrefix="enable-account"
                        onCancel={() => {
                            setEnableAccountDialog(null)
                            setAccountActionReason('')
                        }}
                        saving={enableAccountMutation.isPending}
                        saveLabel={i18n.accounts.enableAccount}
                        saveType="button"
                        onSave={() => {
                            if (!enableAccountDialog) return
                            trackProcessingAccount(enableAccountDialog.id)
                            enableAccountMutation.mutate(enableAccountDialog.id)
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Delete Account Dialog */}
            <Dialog open={!!deleteAccountDialog} onOpenChange={(open) => {
                if (!open) {
                    setDeleteAccountDialog(null)
                    setAccountActionReason('')
                }
            }}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {deleteAccountDialog?.status === AccountStatus.PENDING
                                ? i18n.accounts.cancelCreationTitle
                                : i18n.accounts.deleteAccountTitle}
                        </DialogTitle>
                        <DialogDescription>
                            {deleteAccountDialog?.status === AccountStatus.PENDING
                                ? i18n.accounts.cancelCreationMessage({
                                    systemName: deleteAccountDialog?.targetSystem.displayName || '',
                                })
                                : i18n.accounts.deleteAccountMessage({
                                    systemName: deleteAccountDialog?.targetSystem.displayName || '',
                                })}
                        </DialogDescription>
                    </DialogHeader>
                    <ReasonField
                        value={accountActionReason}
                        onChange={setAccountActionReason}
                        testIdPrefix="delete-account"
                    />
                    <SaveCancelFooter
                        testIdPrefix="delete-account"
                        onCancel={() => {
                            setDeleteAccountDialog(null)
                            setAccountActionReason('')
                        }}
                        saving={deleteAccountMutation.isPending}
                        saveLabel={deleteAccountDialog?.status === AccountStatus.PENDING
                            ? i18n.accounts.cancelAccountCreation
                            : i18n.accounts.deleteAccount}
                        destructive
                        saveType="button"
                        onSave={() => {
                            if (!deleteAccountDialog) return
                            trackProcessingAccount(deleteAccountDialog.id)
                            deleteAccountMutation.mutate(deleteAccountDialog.id)
                        }}
                    />
                </DialogContent>
            </Dialog>

            {/* Access Profile Assignments Section */}
            <CardSection
                icon={Shield}
                title={String(i18n.identities.accessProfiles)}
                description={String(i18n.identities.activeAssignmentCount({count: assignments.filter(a => a.status === AssignmentStatus.ACTIVE).length}))}
                data-testid="section-access-profiles"
            >
                    <Tabs value={accessTab} onValueChange={(v) => setAccessTab(v as 'assignments' | 'timeline')}>
                        <TabsList className="mb-4">
                            <TabsTrigger value="assignments">{i18n.identities.byAssignment}</TabsTrigger>
                            <TabsTrigger value="timeline" data-testid="tab-timeline">{i18n.identities.timeline}</TabsTrigger>
                        </TabsList>

                        <TabsContent value="assignments">
                            {/* Inherited assignments error banner */}
                            {inheritedError && (
                                <>
                                    <MutedText className="text-destructive">{String(i18n.identityInheritedAssignments.errorBanner)}</MutedText>
                                    <Button variant="outline" size="sm" className="ml-2" onClick={() => inheritedRefetch()} data-testid="btn-retry-inherited">
                                        {i18n.common.retry}
                                    </Button>
                                </>
                            )}
                            {assignments.length > 0 || inheritedAssignments.length > 0 || inheritedLoading ? (
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead data-testid="col-expand" className="w-8"></TableHead>
                                            <TableHead data-testid="col-access-profiles">{i18n.identities.accessProfiles}</TableHead>
                                            <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                                            <TableHead data-testid="col-sources">{i18n.identities.sources}</TableHead>
                                            <TableHead data-testid="col-expires-at">{i18n.identities.expiresAt}</TableHead>
                                            {canRevokeAssignment &&
                                                <TableHead data-testid="col-actions" className="w-[80px]">{i18n.common.actions}</TableHead>}
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {mergedAssignments.map((mergedRow) => {
                                            if (mergedRow.kind === 'viaFg') {
                                                const inherited = mergedRow.data
                                                return (
                                                    <TableRow key={`inherited-${inherited.accessProfile.id}-${inherited.functionGroup.id}`} data-testid="inherited-assignment-row">
                                                        <TableCell className="w-8"></TableCell>
                                                        <TableCell>
                                                            <TextLink
                                                                to={routes.accessGovernance.profiles.detail(inherited.accessProfile.id)}
                                                                variant="bold"
                                                                stopPropagation
                                                                testId={`link-inherited-ap-${inherited.accessProfile.id}`}
                                                            >
                                                                {inherited.accessProfile.displayName}
                                                            </TextLink>
                                                            {inherited.resource && (
                                                                <MutedText size="sm" block>{inherited.resource.displayName}</MutedText>
                                                            )}
                                                            <MutedText size="xs" block flex className="mt-0.5">
                                                                <Icon icon={CornerDownRight} size="xs"/>
                                                                <span>{String(i18n.identityInheritedAssignments.viaPrefix)}</span>
                                                                <Button
                                                                    variant="link"
                                                                    size="sm"
                                                                    className="h-auto p-0"
                                                                    onClick={(e) => {
                                                                        e.preventDefault()
                                                                        e.stopPropagation()
                                                                        scrollToFgAssignment(inherited.functionGroup.id)
                                                                    }}
                                                                    data-testid={`link-inherited-via-fg-${inherited.functionGroup.id}`}
                                                                >
                                                                    {inherited.functionGroup.displayName}
                                                                </Button>
                                                            </MutedText>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge variant={
                                                                inherited.status === AccessProfileStatus.ACTIVE ? BadgeVariant.SUCCESS :
                                                                    inherited.status === AccessProfileStatus.DEPRECATED ? BadgeVariant.SECONDARY :
                                                                        BadgeVariant.OUTLINE
                                                            } data-testid={`assignment-status-${inherited.status}`}>
                                                                {labelOf(accessProfileStatuses, inherited.status)}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Badge
                                                                variant="outline"
                                                                className="gap-1 w-fit"
                                                                title={String(i18n.identityInheritedAssignments.tooltip)}
                                                                data-testid={`inherited-source-badge-${inherited.functionGroup.id}`}
                                                            >
                                                                <Icon icon={Layers} size="xs"/>
                                                                {String(i18n.identityInheritedAssignments.sourceLabel)}
                                                            </Badge>
                                                        </TableCell>
                                                        <TableCell>
                                                            {isMaxInstant(inherited.expiresAt) ? (
                                                                <MutedText>{i18n.common.permanent}</MutedText>
                                                            ) : (
                                                                <IconText icon={Clock} size="sm">
                                                                    {formatExpiresAt(inherited.expiresAt, i18n)}
                                                                </IconText>
                                                            )}
                                                        </TableCell>
                                                        {canRevokeAssignment && <TableCell></TableCell>}
                                                    </TableRow>
                                                )
                                            }
                                            const assignment = mergedRow.data
                                            return (
                                            <Fragment key={assignment.id}>
                                                <TableRow
                                                    className="cursor-pointer hover:bg-muted/50"
                                                    onClick={() => toggleAssignment(assignment.id)}
                                                    data-testid="assignment-row"
                                                >
                                                    <TableCell className="w-8">
                                                        {expandedAssignmentId === assignment.id ? (
                                                            <Icon icon={ChevronDown} muted/>
                                                        ) : (
                                                            <Icon icon={ChevronRight} muted/>
                                                        )}
                                                    </TableCell>
                                                    <TableCell>
                                                        <TextLink
                                                            to={routes.accessGovernance.profiles.detail(assignment.accessProfileId)}
                                                            variant="bold"
                                                            stopPropagation
                                                            testId={`link-access-profile-${assignment.accessProfileId}`}
                                                        >
                                                            {assignment.accessProfileName}
                                                        </TextLink>
                                                        <MutedText block>
                                                            {assignment.systemName} / {assignment.resourceName}
                                                        </MutedText>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant={
                                                            assignment.status === AssignmentStatus.ACTIVE ? BadgeVariant.SUCCESS :
                                                                assignment.status === AssignmentStatus.PENDING ? BadgeVariant.SECONDARY :
                                                                    assignment.status === AssignmentStatus.EXPIRED ? BadgeVariant.OUTLINE :
                                                                        BadgeVariant.DESTRUCTIVE
                                                        } className="gap-1" data-testid={`assignment-status-${assignment.status}`}>
                                                            {assignment.status === AssignmentStatus.ACTIVE ? <CheckCircle className="h-3 w-3" aria-hidden="true"/> :
                                                                assignment.status === AssignmentStatus.PENDING ? <Clock className="h-3 w-3" aria-hidden="true"/> :
                                                                    assignment.status === AssignmentStatus.EXPIRED ? <Clock className="h-3 w-3" aria-hidden="true"/> :
                                                                        <XCircle className="h-3 w-3" aria-hidden="true"/>}
                                                            {labelOf(assignmentStatuses, assignment.status)}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell>
                                                        <InlineRow gap="xs" wrap>
                                                            {assignment.sources.filter(s => s.active).length === 0 ? (
                                                                <MutedText>{i18n.identities.activeSources({count: 0})}</MutedText>
                                                            ) : (
                                                                assignment.sources.filter(s => s.active).map((source) => {
                                                                    const SourceIcon = assignmentSourceTypeIcon(source.sourceType)
                                                                    return (
                                                                        <Badge
                                                                            key={source.id}
                                                                            variant="outline"
                                                                            className="gap-1 text-xs"
                                                                            data-testid={`source-badge-${source.id}`}
                                                                        >
                                                                            <Icon icon={SourceIcon} size="xs"/>
                                                                            {source.sourceType === AssignmentSourceType.RULE ? (
                                                                                <Link
                                                                                    to={routes.accessGovernance.rules.detail(source.ruleId!)}
                                                                                    className="hover:underline"
                                                                                    onClick={(e) => e.stopPropagation()}
                                                                                    data-testid={`link-rule-detail-${source.ruleId}`}
                                                                                >
                                                                                    {source.ruleName}
                                                                                </Link>
                                                                            ) : (
                                                                                sourceTypeLabel(source.sourceType)
                                                                            )}
                                                                        </Badge>
                                                                    )
                                                                })
                                                            )}
                                                        </InlineRow>
                                                    </TableCell>
                                                    <TableCell>
                                                        {assignment.permanent ? (
                                                            <MutedText>{i18n.common.permanent}</MutedText>
                                                        ) : (
                                                            <IconText icon={Clock} size="sm">
                                                                {formatExpiresAt(assignment.expiresAt, i18n)}
                                                            </IconText>
                                                        )}
                                                    </TableCell>
                                                    {canRevokeAssignment && (
                                                        <TableCell>
                                                            {assignment.status === AssignmentStatus.ACTIVE && (
                                                                <Button
                                                                    variant="ghost"
                                                                    size="sm"
                                                                    onClick={(e) => {
                                                                        e.stopPropagation()
                                                                        setRevokeAssignmentId(assignment.id)
                                                                    }}
                                                                    className="text-destructive hover:text-destructive"
                                                                    title={String(i18n.identities.revokeAssignment)}
                                                                    data-testid={`btn-revoke-assignment-${assignment.id}`}
                                                                >
                                                                    <XCircle className="h-4 w-4"/>
                                                                </Button>
                                                            )}
                                                        </TableCell>
                                                    )}
                                                </TableRow>
                                                {expandedAssignmentId === assignment.id && (
                                                    <TableRow key={`${assignment.id}-events`}>
                                                        <TableCell colSpan={canRevokeAssignment ? 6 : 5}
                                                                   className="bg-muted/30 p-0">
                                                            <Stack className="p-4">
                                                                                <IconText icon={History} className="mb-3 font-medium">
                                                                    {i18n.identities.assignmentHistory}
                                                                </IconText>
                                                                {assignment.events && assignment.events.length > 0 ? (
                                                                    <Stack gap="sm">
                                                                        {assignment.events.map((event) => {
                                                                            const SourceIcon = event.sourceType ? assignmentSourceTypeIcon(event.sourceType) : null
                                                                            return (
                                                                                <HistoryEntry key={event.id}>
                                                                                    <MutedText size="xs" className="whitespace-nowrap">
                                                                                        {formatDate(event.createdAt)}
                                                                                    </MutedText>
                                                                                    <Strong
                                                                                        weight="medium"
                                                                                        className={assignmentEventTypeColor(event.eventType)}
                                                                                        data-testid={`event-type-${event.eventType}`}
                                                                                    >
                                                                                        {eventTypeLabel(event.eventType)}
                                                                                    </Strong>
                                                                                    <MutedText className="flex-1">
                                                                                        {event.eventType === AssignmentEventType.CREATED && event.newExpiresAt && (
                                                                                            <>{i18n.identities.expiresAt}: {formatExpiresAt(event.newExpiresAt, i18n)}</>
                                                                                        )}
                                                                                        {(event.eventType === AssignmentEventType.EXTENDED || event.eventType === AssignmentEventType.SHORTENED) && (
                                                                                            <>
                                                                                                {formatExpiresAt(event.oldExpiresAt, i18n, true)}
                                                                                                {' \u2192 '}
                                                                                                {formatExpiresAt(event.newExpiresAt, i18n, true)}
                                                                                            </>
                                                                                        )}
                                                                                        {(event.eventType === AssignmentEventType.SOURCE_ADDED || event.eventType === AssignmentEventType.SOURCE_REMOVED || event.eventType === AssignmentEventType.SOURCE_RESTORED) && event.sourceName && SourceIcon && (
                                                                                            <>
                                                                                                <Icon icon={SourceIcon} size="xs" className="inline mr-1"/>
                                                                                                {event.sourceName}
                                                                                            </>
                                                                                        )}
                                                                                        {event.performedBy && (
                                                                                            <SmallText className="ml-1">
                                                                                                {i18n.common.byUser({user: event.performedBy.displayName})}
                                                                                            </SmallText>
                                                                                        )}
                                                                                        {event.comment && <SmallText
                                                                                            className="ml-2 italic">"{event.comment}"</SmallText>}
                                                                                    </MutedText>
                                                                                </HistoryEntry>
                                                                            )
                                                                        })}
                                                                    </Stack>
                                                                ) : (
                                                                    <p><MutedText>{i18n.identities.noHistoryEvents}</MutedText></p>
                                                                )}
                                                            </Stack>
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </Fragment>
                                            )
                                        })}
                                        {inheritedLoading && (
                                            <>
                                                {[0, 1].map(i => (
                                                    <TableRow key={`inherited-skeleton-${i}`}>
                                                        <TableCell className="w-8"></TableCell>
                                                        <TableCell><Skeleton className="h-4 w-32"/></TableCell>
                                                        <TableCell><Skeleton className="h-4 w-16"/></TableCell>
                                                        <TableCell><Skeleton className="h-4 w-24"/></TableCell>
                                                        <TableCell><Skeleton className="h-4 w-20"/></TableCell>
                                                        {canRevokeAssignment && <TableCell></TableCell>}
                                                    </TableRow>
                                                ))}
                                            </>
                                        )}
                                    </TableBody>
                                </Table>
                            ) : (
                                <MutedText block centered className="py-4">
                                    <MutedText>{i18n.identities.noAssignments}</MutedText>
                                </MutedText>
                            )}
                        </TabsContent>

                        <TabsContent value="timeline">
                            <AccessTimeline
                                events={timeline}
                                isLoading={timelineLoading}
                                onEventClick={(event) => setSelectedTimelineEvent(event)}
                            />
                        </TabsContent>
                    </Tabs>
            </CardSection>

            {/* Lifecycle Timeline Section (spec §8.1.2) */}
            <div ref={lifecycleCardRef}>
            <CardSection
                icon={History}
                title={String(i18n.lifecycleEvents.tab.identity)}
                description={String(i18n.lifecycleEvents.empty.cutoverNote)}
                data-testid="card-section-lifecycle"
                data-help-prefix="identityLifecycleTimeline"
            >
                <Tabs
                    value={lifecycleTab}
                    onValueChange={(v) => setLifecycleTab(v as 'overview' | 'lifecycle')}
                >
                    <TabsList className="mb-4">
                        <TabsTrigger value="overview">{i18n.common.details}</TabsTrigger>
                        <TabsTrigger value="lifecycle" data-testid="tab-lifecycle">
                            {i18n.lifecycleEvents.tab.identity}
                        </TabsTrigger>
                    </TabsList>
                    <TabsContent value="overview">
                        <IdentityLifecycleHeader
                            event={lifecycleHeaderQuery.data?.content?.[0]}
                            isLoading={lifecycleHeaderQuery.isLoading}
                            isError={lifecycleHeaderQuery.isError}
                            onViewTimeline={handleViewTimeline}
                            data-testid="lifecycle-section-header"
                        />
                    </TabsContent>
                    <TabsContent value="lifecycle">
                        <IdentityLifecycleTimeline
                            events={lifecycleTimelineQuery.data?.content ?? []}
                            isLoading={lifecycleTimelineQuery.isLoading}
                            isError={lifecycleTimelineQuery.isError}
                            errorObj={lifecycleTimelineQuery.error}
                            filters={lifecycleFilters}
                            onFiltersChange={setLifecycleFilters}
                        />
                    </TabsContent>
                </Tabs>
                <InlineRow gap="sm" wrap className="mt-4">
                    <MutedText size="sm">
                        {i18n.common.createdAt} {formatTimestamp(identity.createdAt)}
                    </MutedText>
                    <MutedText size="sm">
                        {i18n.common.updatedAt} {formatTimestamp(identity.updatedAt)}
                    </MutedText>
                </InlineRow>
            </CardSection>
            </div>

            {/* Function Group Assignments Section */}
            <CardSection
                icon={ListTree}
                title={String(i18n.functionGroupAssignments.title)}
                description={String(i18n.functionGroupAssignments.activeAssignmentCount({count: fgAssignments.filter(a => a.status === FunctionGroupAssignmentStatus.ACTIVE).length}))}
                data-testid="card-section-function-groups"
            >
                    {fgAssignments.length > 0 ? (
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead data-testid="col-fg-expand" className="w-8"></TableHead>
                                    <TableHead data-testid="col-function-group">{i18n.functionGroupAssignments.functionGroup}</TableHead>
                                    <TableHead data-testid="col-fg-status">{i18n.common.status}</TableHead>
                                    <TableHead data-testid="col-fg-sources">{i18n.functionGroupAssignments.sources}</TableHead>
                                    <TableHead data-testid="col-fg-expires-at">{i18n.functionGroupAssignments.expiresAt}</TableHead>
                                    {canAssignFg &&
                                        <TableHead data-testid="col-fg-actions" className="w-[80px]">{i18n.common.actions}</TableHead>}
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {fgAssignments.map((fgAssignment) => (
                                    <Fragment key={fgAssignment.id}>
                                        <ClickableTableRow
                                            id={`fg-assignment-${fgAssignment.functionGroupId}`}
                                            className="scroll-mt-20"
                                            onClick={() => toggleFgAssignment(fgAssignment.id)}
                                            data-testid={`fg-assignment-row-${fgAssignment.functionGroupId}`}
                                        >
                                            <TableCell className="w-8">
                                                {expandedFgAssignmentId === fgAssignment.id ? (
                                                    <Icon icon={ChevronDown} muted/>
                                                ) : (
                                                    <Icon icon={ChevronRight} muted/>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                <TextLink
                                                    to={routes.accessGovernance.functionGroups.detail(fgAssignment.functionGroupId)}
                                                    variant="bold"
                                                    stopPropagation
                                                    testId={`link-fg-detail-${fgAssignment.functionGroupId}`}
                                                >
                                                    {fgAssignment.functionGroupName}
                                                </TextLink>
                                            </TableCell>
                                            <TableCell>
                                                {(() => {
                                                    const statusBadge = FunctionGroupAssignmentStatusBadge.of(fgAssignment.status)
                                                    const StatusIcon = statusBadge?.icon
                                                    return (
                                                        <Badge variant={statusBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1"
                                                               data-testid={`fg-assignment-status-${fgAssignment.status}`}>
                                                            {StatusIcon && <StatusIcon className="h-3 w-3" aria-hidden="true"/>}
                                                            {fgStatusLabel(fgAssignment.status)}
                                                        </Badge>
                                                    )
                                                })()}
                                            </TableCell>
                                            <TableCell>
                                                <InlineRow gap="xs" wrap>
                                                    {fgAssignment.sources.filter(s => !s.removedAt).length === 0 ? (
                                                        <MutedText>{i18n.functionGroupAssignments.activeSources({count: 0})}</MutedText>
                                                    ) : (
                                                        fgAssignment.sources.filter(s => !s.removedAt).map((source) => {
                                                            const SourceIcon = assignmentSourceTypeIcon(source.sourceType)
                                                            return (
                                                                <Badge
                                                                    key={source.id}
                                                                    variant="outline"
                                                                    className="gap-1 text-xs"
                                                                    data-testid={`source-badge-${source.id}`}
                                                                >
                                                                    <Icon icon={SourceIcon} size="xs"/>
                                                                    {sourceTypeLabel(source.sourceType)}
                                                                </Badge>
                                                            )
                                                        })
                                                    )}
                                                </InlineRow>
                                            </TableCell>
                                            <TableCell>
                                                {fgAssignment.permanent ? (
                                                    <MutedText>{i18n.common.permanent}</MutedText>
                                                ) : (
                                                    <IconText icon={Clock} size="sm">
                                                        {formatExpiresAt(fgAssignment.expiresAt, i18n)}
                                                    </IconText>
                                                )}
                                            </TableCell>
                                            {canAssignFg && (
                                                <TableCell>
                                                    {fgAssignment.status === FunctionGroupAssignmentStatus.ACTIVE && (
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={(e) => {
                                                                e.stopPropagation()
                                                                setRevokeFgAssignmentId(fgAssignment.id)
                                                            }}
                                                            className="text-destructive hover:text-destructive"
                                                            title={String(i18n.functionGroupAssignments.revokeAssignment)}
                                                            data-testid={`btn-revoke-fg-assignment-${fgAssignment.id}`}
                                                        >
                                                            <XCircle className="h-4 w-4"/>
                                                        </Button>
                                                    )}
                                                </TableCell>
                                            )}
                                        </ClickableTableRow>
                                        {expandedFgAssignmentId === fgAssignment.id && (
                                            <TableRow key={`${fgAssignment.id}-events`}>
                                                <TableCell colSpan={canAssignFg ? 6 : 5} className="bg-muted/30 p-0">
                                                    <Stack className="p-4">
                                                        <IconText icon={History} className="mb-3 font-medium">
                                                            {i18n.functionGroupAssignments.assignmentHistory}
                                                        </IconText>
                                                        {fgAssignment.events && fgAssignment.events.length > 0 ? (
                                                            <Stack gap="sm">
                                                                {fgAssignment.events.map((event) => (
                                                                    <HistoryEntry key={event.id}>
                                                                        <MutedText size="xs" className="whitespace-nowrap">
                                                                            {formatDate(event.createdAt)}
                                                                        </MutedText>
                                                                        <Strong data-testid={`event-type-${event.eventType}`}>
                                                                            {eventTypeLabel(event.eventType)}
                                                                        </Strong>
                                                                        <MutedText className="flex-1">
                                                                            {event.eventType === AssignmentEventType.CREATED && event.newExpiresAt && (
                                                                                <>{i18n.functionGroupAssignments.expiresAt}: {formatExpiresAt(event.newExpiresAt, i18n)}</>
                                                                            )}
                                                                            {(event.eventType === AssignmentEventType.EXTENDED || event.eventType === AssignmentEventType.SHORTENED) && (
                                                                                <>
                                                                                    {formatExpiresAt(event.oldExpiresAt, i18n, true)}
                                                                                    {' \u2192 '}
                                                                                    {formatExpiresAt(event.newExpiresAt, i18n, true)}
                                                                                </>
                                                                            )}
                                                                            {event.comment && <SmallText className="ml-2 italic">"{event.comment}"</SmallText>}
                                                                        </MutedText>
                                                                    </HistoryEntry>
                                                                ))}
                                                            </Stack>
                                                        ) : (
                                                            <p><MutedText>{i18n.functionGroupAssignments.noHistoryEvents}</MutedText></p>
                                                        )}
                                                    </Stack>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </Fragment>
                                ))}
                            </TableBody>
                        </Table>
                    ) : (
                        <MutedText block centered className="py-4">
                            <MutedText>{i18n.functionGroupAssignments.noAssignments}</MutedText>
                        </MutedText>
                    )}
            </CardSection>

            {/* Timeline Detail Modal */}
            <TimelineDetailModal
                identityId={id!}
                event={selectedTimelineEvent}
                open={selectedTimelineEvent !== null}
                onOpenChange={(open) => !open && setSelectedTimelineEvent(null)}
            />

            {/* Lifecycle Confirmation Dialog */}
            <Dialog open={lifecycleDialog.isOpen} onOpenChange={lifecycleDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>
                            {targetState === IdentityLifecycleState.ACTIVE && i18n.identities.activateIdentity}
                            {targetState === IdentityLifecycleState.SUSPENDED && i18n.identities.suspendIdentity}
                            {targetState === IdentityLifecycleState.TERMINATED && i18n.identities.terminateIdentity}
                        </DialogTitle>
                        <DialogDescription>
                            {targetState === IdentityLifecycleState.ACTIVE && i18n.identities.activateDescription}
                            {targetState === IdentityLifecycleState.SUSPENDED && i18n.identities.suspendDescription}
                            {targetState === IdentityLifecycleState.TERMINATED && i18n.identities.terminateDescription}
                        </DialogDescription>
                    </DialogHeader>
                    {targetState === IdentityLifecycleState.SUSPENDED && (
                        <FormField
                            label={String(i18n.identities.plannedReactivationDate)}
                            htmlFor="input-suspend-reactivation-date"
                        >
                            <DatePicker
                                testId="input-suspend-reactivation-date"
                                value={suspendReactivationDate}
                                onChange={setSuspendReactivationDate}
                                ariaLabel={String(i18n.identities.plannedReactivationDate)}
                            />
                            <MutedText size="xs">{i18n.identities.plannedReactivationDateHelper}</MutedText>
                        </FormField>
                    )}
                    <FormField
                        label={String(i18n.lifecycleEvents.dialog.reasonLabel)}
                        htmlFor="input-lifecycle-reason"
                        data-help-prefix="identityLifecycleTimeline"
                    >
                        <Textarea
                            id="input-lifecycle-reason"
                            value={lifecycleReason}
                            onChange={(e) => setLifecycleReason(e.target.value.slice(0, 1000))}
                            maxLength={1000}
                            placeholder={String(i18n.lifecycleEvents.dialog.reasonPlaceholder)}
                            data-testid="input-lifecycle-reason"
                            data-help-key="reason"
                        />
                        <InlineRow gap="sm" justify="between">
                            <MutedText size="xs">{i18n.lifecycleEvents.dialog.reasonHelp}</MutedText>
                            <MutedText
                                size="xs"
                                className={lifecycleReason.length >= 1000 ? 'text-destructive' : undefined}
                                data-testid="lifecycle-reason-counter"
                            >
                                {String(i18n.lifecycleEvents.dialog.reasonCounter({count: lifecycleReason.length}))}
                            </MutedText>
                        </InlineRow>
                    </FormField>
                    <SaveCancelFooter
                        testIdPrefix="lifecycle"
                        onCancel={lifecycleDialog.close}
                        saving={lifecycleMutation.isPending}
                        saveLabel={i18n.common.confirm}
                        savingLabel={i18n.identities.processing}
                        destructive={targetState === IdentityLifecycleState.SUSPENDED || targetState === IdentityLifecycleState.TERMINATED}
                        saveType="button"
                        onSave={confirmLifecycleChange}
                    />
                </DialogContent>
            </Dialog>

            {/* Reactivation Date Edit Dialog */}
            <Dialog open={reactivationDateDialog.isOpen} onOpenChange={reactivationDateDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.identities.editReactivationDate}</DialogTitle>
                        <DialogDescription>{i18n.identities.editReactivationDateDescription}</DialogDescription>
                    </DialogHeader>
                    <FormField
                        label={String(i18n.identities.plannedReactivationDate)}
                        htmlFor="input-edit-reactivation-date"
                    >
                        <DatePicker
                            testId="input-edit-reactivation-date"
                            value={reactivationDateDraft}
                            onChange={setReactivationDateDraft}
                            ariaLabel={String(i18n.identities.plannedReactivationDate)}
                        />
                        <MutedText size="xs">{i18n.identities.plannedReactivationDateHelper}</MutedText>
                    </FormField>
                    <SaveCancelFooter
                        testIdPrefix="reactivation-date"
                        onCancel={reactivationDateDialog.close}
                        saving={reactivationDateMutation.isPending}
                        saveLabel={i18n.common.save}
                        savingLabel={i18n.identities.processing}
                        saveType="button"
                        onSave={submitReactivationDate}
                    />
                </DialogContent>
            </Dialog>

            {/* Rehire Dialog (TERMINATED → SUSPENDED awaiting entry, or ACTIVE) */}
            <Dialog open={rehireDialog.isOpen} onOpenChange={rehireDialog.bind.onOpenChange}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.identities.rehireIdentity}</DialogTitle>
                        <DialogDescription>{i18n.identities.rehireDescription}</DialogDescription>
                    </DialogHeader>
                    <Stack gap="md">
                        <FormField
                            label={String(i18n.identities.rehireNewEntryDate)}
                            htmlFor="input-rehire-new-entry-date"
                            required
                        >
                            <DatePicker
                                testId="input-rehire-new-entry-date"
                                value={rehireNewEntryDate}
                                onChange={setRehireNewEntryDate}
                                ariaLabel={String(i18n.identities.rehireNewEntryDate)}
                            />
                            <MutedText size="xs">{i18n.identities.rehireNewEntryDateHelper}</MutedText>
                        </FormField>
                        <FormField
                            label={String(i18n.identities.rehireReason)}
                            htmlFor="input-rehire-reason"
                            required
                        >
                            <Input
                                id="input-rehire-reason"
                                type="text"
                                value={rehireReason}
                                onChange={(e) => setRehireReason(e.target.value)}
                                data-testid="input-rehire-reason"
                                maxLength={500}
                            />
                            <MutedText size="xs">{i18n.identities.rehireReasonHelper}</MutedText>
                        </FormField>
                    </Stack>
                    <SaveCancelFooter
                        testIdPrefix="rehire"
                        onCancel={rehireDialog.close}
                        saving={rehireMutation.isPending}
                        saveLabel={i18n.identities.rehire}
                        savingLabel={i18n.identities.processing}
                        saveType="button"
                        onSave={submitRehire}
                    />
                </DialogContent>
            </Dialog>

            {/* Revoke Assignment Confirmation Dialog */}
            <Dialog open={!!revokeAssignmentId} onOpenChange={(open) => !open && setRevokeAssignmentId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.identities.revokeAssignment}</DialogTitle>
                        <DialogDescription>
                            {i18n.identities.revokeAssignmentConfirm}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="revoke"
                        onCancel={() => setRevokeAssignmentId(null)}
                        saving={revokeMutation.isPending}
                        saveLabel={i18n.identities.revoke}
                        savingLabel={i18n.identities.revoking}
                        destructive
                        saveType="button"
                        onSave={() => revokeAssignmentId && revokeMutation.mutate(revokeAssignmentId)}
                    />
                </DialogContent>
            </Dialog>

            {/* Revoke FG Assignment Confirmation Dialog */}
            <Dialog open={!!revokeFgAssignmentId} onOpenChange={(open) => !open && setRevokeFgAssignmentId(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>{i18n.functionGroupAssignments.revokeAssignment}</DialogTitle>
                        <DialogDescription>
                            {i18n.functionGroupAssignments.revokeAssignmentConfirm}
                        </DialogDescription>
                    </DialogHeader>
                    <SaveCancelFooter
                        testIdPrefix="revoke-fg"
                        onCancel={() => setRevokeFgAssignmentId(null)}
                        saving={revokeFgMutation.isPending}
                        saveLabel={i18n.functionGroupAssignments.revoke}
                        savingLabel={i18n.functionGroupAssignments.revoking}
                        destructive
                        saveType="button"
                        onSave={() => revokeFgAssignmentId && revokeFgMutation.mutate(revokeFgAssignmentId)}
                    />
                </DialogContent>
            </Dialog>
        </>
        )}
        </PageLayout>
    )
}
